import { AfterViewInit, ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { DataTableHeaderOption } from '@dxo/ng-swift-ui';
import {
  MemberClient,
  MemberListSearchResponse,
  MemberStatus,
  SearchSortField,
  SearchSortDirection,
  MemberListSearchRequestStatus,
  MemberInfoResponseStatus
} from '@benefitsui/app/api/benefits-clients';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { FormGroup, UntypedFormBuilder } from '@angular/forms';
import { DropdownOption } from '@benefitsui/app/lib/common.types';
import { MemberData, MemberInfo, MemberListSearchParameters } from '../employee-page.types';
import { EmployeeService } from '../employee.service';
import { WINDOW } from '@benefitsui/app/lib/tokens/window';
import { Defaults } from '@benefitsui/app/shared/defaults/defaults';

@Component({
  selector: 'app-employees',
  templateUrl: './list.component.html',
  styleUrls: ['./list.component.scss'],
  standalone: false
})
export class ListEmployeeComponent implements OnInit, AfterViewInit {
  constructor(
    @Inject(WINDOW) private window: Window,
    private memberClient: MemberClient,
    private employeeService: EmployeeService,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef
  ) {
    this.formGroup = this.fb.group({
      employeeName: Defaults.stringEmpty,
      status: Defaults.stringEmpty,
      billingDivision: Defaults.stringEmpty
    });
  }

  // :  Data members
  formGroup: FormGroup;

  StatusOptions: DropdownOption<string>[] = [
    { key: MemberStatus.Unknown, displayName: 'All' },
    { key: MemberStatus.Active, displayName: MemberStatus.Active },
    { key: MemberStatus.Terminated, displayName: MemberStatus.Terminated },
    {
      key: MemberStatus.Planned_termination,
      displayName: MemberStatus.Planned_termination
    }
  ];
  billLocationOptions: DropdownOption<string>[] = [];

  isLoading = true;

  searchParameter: MemberListSearchParameters = {
    groupId: undefined,
    policyId: undefined,
    employeeName: undefined,
    status: MemberListSearchRequestStatus.Unknown,
    billingDivision: undefined,
    page: 1,
    pageSize: 10,
    sortField: SearchSortField.BillingDivision,
    sortDirection: SearchSortDirection.Ascending
  };

  totalLines = 0;

  tableHeaders: DataTableHeaderOption[] = [
    { text: 'Employee name', key: 'employeeName', width: '40%' },
    { text: 'Status', key: 'status', width: '20%' },
    { text: 'Billing division', key: 'billingDivision', width: '40%' }
  ];

  employeeName$ = new BehaviorSubject<string | null>(Defaults.stringEmpty);
  status$ = new BehaviorSubject<string | null>(MemberInfoResponseStatus.Active);
  billingDivision$ = new BehaviorSubject<string | null>('All');
  private pageSize$ = new BehaviorSubject<number>(10);
  private page$ = new BehaviorSubject<number>(1);
  private sortDirection$ = new BehaviorSubject<SearchSortDirection>(SearchSortDirection.Ascending);
  private sortField$ = new BehaviorSubject<SearchSortField>(SearchSortField.LastName);

  members$ = combineLatest([
    this.page$.pipe(distinctUntilChanged()),
    this.pageSize$.pipe(distinctUntilChanged()),
    this.sortDirection$.pipe(distinctUntilChanged()),
    this.sortField$.pipe(distinctUntilChanged()),
    this.employeeName$.pipe(distinctUntilChanged()),
    this.status$.pipe(distinctUntilChanged()),
    this.billingDivision$.pipe(distinctUntilChanged())
  ])
    .pipe(
      switchMap((vals) => {
        this.searchParameter.page = vals[0];
        this.searchParameter.pageSize = vals[1];
        this.searchParameter.sortDirection = vals[2];
        this.searchParameter.sortField = vals[3];
        this.searchParameter.employeeName = vals[4] as string;
        this.searchParameter.status = vals[5] as MemberListSearchRequestStatus;
        this.searchParameter.billingDivision = vals[6] as string;

        return this.memberClient
          .list({
            groupId: undefined,
            sortDirection: this.searchParameter.sortDirection,
            policyId: undefined,
            employeeName: this.searchParameter.employeeName,
            status: this.searchParameter.status ?? MemberListSearchRequestStatus.Unknown,
            billingDivision: this.searchParameter.billingDivision,
            page: this.searchParameter.page!,
            pageSize: this.searchParameter.pageSize!,
            sortField: this.searchParameter.sortField
          })
          .pipe(map((x) => x.data));
      })
    )
    .pipe(
      tap((x) => {
        this.isLoading = false;
        this.totalLines = x?.totalResults ?? 0;
      }),
      map((x) => this.transformData(x))
    );

  ngOnInit(): void {
    if (this.formGroup) {
      const employeeName = this.formGroup.get('employeeName');
      const billingDivision = this.formGroup.get('billingDivision');
      const status = this.formGroup.get('status');

      employeeName!.valueChanges.subscribe((value) => {
        this.employeeName$.next(value);
      });
      status!.valueChanges.subscribe((value) => {
        this.status$.next(value);
      });
      billingDivision!.valueChanges.subscribe((value) => {
        this.billingDivision$.next(value);
      });
    }
  }

  transformData(response: MemberListSearchResponse | undefined): MemberData {
    const members: MemberInfo[] = [];
    response?.memberListResponse?.members!.map((member) => {
      members.push({
        employeeId: member.employeeId ?? Defaults.stringEmpty,
        contractId: member.contractId ?? Defaults.stringEmpty,
        employeeName: member.employeeName ?? Defaults.stringEmpty,
        firstName: member.firstName ?? Defaults.stringEmpty,
        lastName: member.lastName ?? Defaults.stringEmpty,
        applicantName: member.applicantName ?? Defaults.stringEmpty,
        dob: member.dob ?? Defaults.stringEmpty,
        status: member.status ?? MemberInfoResponseStatus.Unknown,
        coverage: member.coverage ?? Defaults.stringEmpty,
        billingDivision: member.billingDivision ?? Defaults.stringEmpty,
        indicator: this.getStatusColor(member.status ?? Defaults.stringEmpty)
      });
      this.billLocationOptions = (response?.billingDivisionList ?? []).map((billingDivisionList) => ({
        key: billingDivisionList,
        displayName: billingDivisionList
      }));
    });
    return {
      totalResults: response?.totalResults ?? 0,
      listMember: members ?? []
    };
  }

  ngAfterViewInit() {
    this.cd.detectChanges();
  }

  async submit() {
    console.log('Submitted');
  }

  onClickTableHeader(sortField: string) {
    let newSortDirection = SearchSortDirection.Descending;
    if (this.searchParameter.sortDirection === SearchSortDirection.Descending) {
      newSortDirection = SearchSortDirection.Ascending;
    }
    const field: SearchSortField = SearchSortField[sortField as keyof typeof SearchSortField];

    this.sortDirection$.next(newSortDirection);
    this.sortField$.next(field);
  }

  onFilterChange(value: string, filterType: string) {
    if (filterType === 'employeeName') {
      this.employeeName$.next(value);
    } else if (filterType === 'status') {
      this.status$.next(value);
    } else if (filterType === 'billingDivision') {
      this.billingDivision$.next(value);
    }
  }

  clearFilters() {
    this.formGroup.setValue({
      employeeName: Defaults.stringEmpty,
      status: 'Active',
      billingDivision: 'All'
    });
  }

  onPageChange(page: number) {
    this.page$.next(page);
  }

  onPageSizeChange(pageSize: number) {
    this.pageSize$.next(pageSize);
  }

  statusColors = {
    red: '#D32F2F',
    green: '#8ABF2E',
    yellow: '#FDD835'
  };

  getStatusColor(status: string | undefined) {
    switch (status) {
      case MemberStatus.Terminated:
        return this.statusColors.red;
      case MemberStatus.Active:
        return this.statusColors.green;
      case MemberStatus.Planned_termination:
        return this.statusColors.yellow;
      default:
        return Defaults.stringEmpty;
    }
  }

  get token() {
    return this.employeeService.token;
  }
}
